// src/lib/cache/invoices.js
//
// Per-entity cache wiring for invoices. Uses the deferredField hybrid:
// invoice_number is generated inside createInvoice (reads max from DB,
// increments year-scoped counter; UNIQUE(invoice_number, company_id) plus
// retry on collision). The optimistic UI must NOT predict a number — a
// predicted number can be wrong-but-still-succeed in a multi-tab race and
// the optimistic row would visibly flip on reconcile.

import {
  getInvoices,
  createInvoice,
  updateInvoice,
  deleteInvoice,
} from '../api/financial/invoices.js'
import {
  useEntityListQuery,
  useOptimisticListMutation,
  useStandardDeleteMutation,
  defaultCreateReconcile,
  defaultUpdateReconcile,
  unwrapList,
} from './entityCache.js'

const ENTITY = 'invoices'
const DEFERRED_NUMBER = 'invoice_number'
const NUMBER_PLACEHOLDER = '…'

export const useInvoicesQuery = () =>
  useEntityListQuery(ENTITY, unwrapList('invoices', getInvoices))

export const useCreateInvoiceMutation = () =>
  useOptimisticListMutation(ENTITY, {
    deferredField: DEFERRED_NUMBER,
    mutationFn: async (invoiceData) => {
      const { invoice_number: _ignored, invoiceNumber: _ignored2, ...safe } = invoiceData
      return createInvoice(safe)
    },
    applyOptimistic: (prev, vars, { tempId }) => {
      const now = new Date().toISOString()
      const { invoice_number: _ignored, invoiceNumber: _ignored2, ...safe } = vars
      return [{
        status: 'draft',
        ...safe,
        id: tempId,
        invoice_number: NUMBER_PLACEHOLDER,
        created_at: now,
        updated_at: now,
      }, ...prev]
    },
    reconcile: defaultCreateReconcile,
  })

export const useUpdateInvoiceMutation = () =>
  useOptimisticListMutation(ENTITY, {
    mutationFn: ({ id, ...patch }) => updateInvoice(id, patch),
    applyOptimistic: (prev, vars) =>
      prev.map((row) => (row.id === vars.id ? { ...row, ...vars } : row)),
    reconcile: defaultUpdateReconcile,
  })

export const useDeleteInvoiceMutation = () =>
  useStandardDeleteMutation(ENTITY, deleteInvoice)
